import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaMapMarkerAlt, FaCalendarAlt, FaTimes } from "react-icons/fa";
import { Card, Badge } from "../../../UI/Card";
import Button from "../../../UI/Button";
import { StarInput } from "../../../Marketplace/StarRating";
import {
  getMyMarketplaceTrips,
  getCancellationPreview,
  cancelMarketplaceBooking,
  submitBookingReview,
  getApiErrorMessage,
} from "../../../Marketplace/services/marketplaceApi";
import {
  formatINR,
  formatDate,
  STATUS_TONE,
  bookingTitle,
} from "../../../Marketplace/marketplaceUtils";

// Confirm-cancel modal — always shows the server-computed refund preview before the customer
// can confirm, never a client-guessed number (per marketplace cancellation policy).
const CancelModal = ({ booking, onClose, onCancelled }) => {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getCancellationPreview(booking._id);
        if (alive) setPreview(res?.data || null);
      } catch (err) {
        if (alive) setError(getApiErrorMessage(err, "Could not load cancellation details."));
      } finally {
        if (alive) setLoadingPreview(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [booking._id]);

  const confirmCancel = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await cancelMarketplaceBooking(booking._id, reason);
      toast.success(res?.message || "Booking cancelled");
      onCancelled();
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to cancel booking."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-5 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-ink-400 hover:text-ink-700"
          aria-label="Close"
        >
          <FaTimes />
        </button>
        <h3 className="text-lg font-bold text-ink-900 mb-1">Cancel booking</h3>
        <p className="text-sm text-ink-500 mb-4">{bookingTitle(booking)}</p>

        {loadingPreview ? (
          <div className="h-20 rounded-xl bg-ink-100 animate-pulse" />
        ) : !preview?.eligible ? (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
            {preview?.reason || "This booking can no longer be cancelled."}
          </p>
        ) : (
          <>
            <div className="rounded-xl bg-ink-50 border border-ink-100 p-3.5 mb-4 text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-ink-500">Refund amount</span>
                <span className="font-bold text-ink-900">{formatINR(preview.refundAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-500">Refund %</span>
                <span className="font-semibold text-ink-700">{preview.refundPercent}%</span>
              </div>
            </div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for cancellation (optional)"
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200 mb-3"
            />
          </>
        )}

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        <div className="flex gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Keep booking
          </Button>
          {preview?.eligible && (
            <Button variant="primary" size="sm" onClick={confirmCancel} loading={submitting}>
              Confirm cancellation
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const ReviewModal = ({ booking, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await submitBookingReview(booking._id, { rating, text: text.trim() });
      toast.success("Thanks for your review!");
      onSubmitted();
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to submit review."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-5 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-ink-400 hover:text-ink-700"
          aria-label="Close"
        >
          <FaTimes />
        </button>
        <h3 className="text-lg font-bold text-ink-900 mb-1">Rate your trip</h3>
        <p className="text-sm text-ink-500 mb-4">{bookingTitle(booking)}</p>

        <div className="mb-4">
          <StarInput value={rating} onChange={setRating} />
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Share details of your experience (optional)"
          rows={4}
          className="w-full px-3 py-2.5 rounded-lg border border-ink-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200 mb-3"
        />

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        <div className="flex gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={submit} loading={submitting}>
            Submit review
          </Button>
        </div>
      </div>
    </div>
  );
};

const MarketplaceTrips = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewedIds, setReviewedIds] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMyMarketplaceTrips();
      setTrips(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load your marketplace trips."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-40 rounded-2xl bg-ink-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
        <span>{error}</span>
        <Button variant="secondary" size="sm" onClick={load}>
          Retry
        </Button>
      </div>
    );
  }

  if (trips.length === 0) {
    return (
      <div className="text-center py-16 text-ink-500">
        <p className="text-lg font-semibold text-ink-700 mb-1">No marketplace trips yet</p>
        <p className="text-sm">Taxis and holiday packages you book will show up here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {trips.map((booking) => {
        const request = booking.travelRequestId;
        const canCancel = ["PendingPayment", "Confirmed"].includes(booking.status);
        const canReview = booking.status === "Completed" && !reviewedIds.has(booking._id);

        return (
          <Card key={booking._id} className="p-4 sm:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <h3 className="font-bold text-ink-900">{bookingTitle(booking)}</h3>
                  <Badge tone={STATUS_TONE[booking.status] || "ink"}>{booking.status}</Badge>
                </div>
                <p className="text-xs text-ink-500 mb-1">
                  {booking.partnerId?.businessName || "Rasutrip Partner"}
                </p>
                {request?.destination && (
                  <p className="flex items-center gap-1.5 text-xs text-ink-500 mb-1">
                    <FaMapMarkerAlt className="shrink-0" /> {request.destination}
                  </p>
                )}
                {request?.startDate && (
                  <p className="flex items-center gap-1.5 text-xs text-ink-500">
                    <FaCalendarAlt className="shrink-0" />
                    {formatDate(request.startDate)}
                    {request.endDate ? ` – ${formatDate(request.endDate)}` : ""}
                  </p>
                )}
                {booking.status === "Cancelled" && (
                  <p className="text-xs text-amber-700 mt-1.5">
                    Refunded {formatINR(booking.refundAmount)} ({booking.refundPercent}%)
                  </p>
                )}
              </div>

              <div className="text-right shrink-0">
                <p className="text-lg font-extrabold text-brand-600 mb-2">
                  {formatINR(booking.grossAmount)}
                </p>
                <div className="flex flex-col gap-2">
                  {canCancel && (
                    <Button variant="secondary" size="sm" onClick={() => setCancelTarget(booking)}>
                      Cancel booking
                    </Button>
                  )}
                  {canReview && (
                    <Button variant="outline" size="sm" onClick={() => setReviewTarget(booking)}>
                      Write a review
                    </Button>
                  )}
                  {booking.status === "Completed" && reviewedIds.has(booking._id) && (
                    <span className="text-xs text-ink-400">Review submitted</span>
                  )}
                </div>
              </div>
            </div>
          </Card>
        );
      })}

      {cancelTarget && (
        <CancelModal
          booking={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onCancelled={() => {
            setCancelTarget(null);
            load();
          }}
        />
      )}

      {reviewTarget && (
        <ReviewModal
          booking={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onSubmitted={() => {
            setReviewedIds((prev) => new Set(prev).add(reviewTarget._id));
            setReviewTarget(null);
          }}
        />
      )}
    </div>
  );
};

export default MarketplaceTrips;
